import type { PropTypes } from './Leaderboard.types';
import type { ReactElement } from 'react';

import { Button, Card, Flex, Heading } from '@rango-dev/whale';
import { ExternalLink } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useAppSelector } from '../../../hooks/reduxHooks';
import { profileSummarySelector } from '../../../state/ProfileSlice/selectors';
import { CompactTopUserCard, CompactTopUserCardSkeleton } from '../../Leaderboard/TopUserCard';
import { UnexpectedError } from '../UnexpectedError';

import styles from './Leaderboard.module.css';
import { List } from './List';

const TOP_USERS_COUNT = 3;
const PUBLIC_SKELETON_ROWS = 7;
const PRIVATE_SKELETON_ROWS = 9;

export function Leaderboard(props: PropTypes): ReactElement {
  const { t } = useTranslation();
  const { title = t('Leaderboard'), list, error, loading, isPublic = false, onRetry } = props;
  const topUsers = isPublic ? list?.slice(0, TOP_USERS_COUNT) : [];
  const otherUsers = isPublic ? list?.slice(TOP_USERS_COUNT) : list;
  const navigate = useNavigate();
  const { username } = useParams();
  const { data: profileSummary } = useAppSelector((state) =>
    profileSummarySelector(state, username),
  );
  const leaderboardLoading = loading || !otherUsers;
  const skeletonRows = isPublic ? PUBLIC_SKELETON_ROWS : PRIVATE_SKELETON_ROWS;

  return (
    <Card variant="surface" size={{ initial: '2', sm: '3' }}>
      <Flex direction="column" gap="5" py={error ? { lg: '158px' } : undefined}>
        {!error && (
          <Heading className="leaderboard-title" size="5" weight="bold">
            {title}
          </Heading>
        )}
        {error ? (
          <UnexpectedError
            context={`${username ? 'profile' : 'login'}-page-leaderboard`}
            onRetry={onRetry}
          />
        ) : (
          <Flex direction="column" gap="5">
            {isPublic && (
              <Flex gap="2" align="stretch" width="100%">
                {leaderboardLoading
                  ? Array.from({ length: TOP_USERS_COUNT }, (_, i) => (
                      <CompactTopUserCardSkeleton key={i} />
                    ))
                  : (topUsers || []).map((user) => (
                      <CompactTopUserCard key={user.id} user={user} type="score" />
                    ))}
              </Flex>
            )}
            <List
              showLevel={!leaderboardLoading}
              avatarSize="3"
              loading={leaderboardLoading}
              maxLengthSkeleton={skeletonRows}
              list={leaderboardLoading ? [] : otherUsers}
              userId={leaderboardLoading ? undefined : profileSummary?.id}
            />
            <Button
              id="profile-page-view-full-leaderboard"
              onClick={() => navigate('/leaderboard/score')}
              variant="outline"
              color="cyan"
              size="3"
              disabled={loading || !list}
              radius="full"
              className={styles.fullLeaderboardButton}>
              <ExternalLink size={16} />
              {t('View full leaderboard')}
            </Button>
          </Flex>
        )}
      </Flex>
    </Card>
  );
}
